import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FileClock } from 'lucide-react'
import type { KeyboardEvent, ReactNode } from 'react'

type PosCompletionDialogProps = {
  open: boolean
  icon: ReactNode
  title: string
  description: string
  /** While the dialog's work is underway (opening the order, preparing an action), its buttons wait. */
  busy: boolean
  onViewOrder: () => void
  /** The way on from here: a new order, or a new change to the order. Enter takes it too. */
  start: { label: string; icon: ReactNode; onClick: () => void }
  /** Further actions, between View Order and the way on. */
  children?: ReactNode
}

/**
 * What the POS shows once its work on an order is done: staff leave it only by viewing the order or taking the way on,
 * never by closing it.
 */
export function PosCompletionDialog({
  open,
  icon,
  title,
  description,
  busy,
  onViewOrder,
  start,
  children,
}: PosCompletionDialogProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || event.repeat || busy) {
      return
    }
    if (
      event.target instanceof Element &&
      event.target.closest('[data-pos-order-complete-action]')
    ) {
      return
    }

    event.preventDefault()
    start.onClick()
  }

  return (
    <Dialog open={open}>
      <DialogContent
        showCloseButton={false}
        onKeyDownCapture={handleKeyDown}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {icon}
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onViewOrder}
            disabled={busy}
            className="gap-2"
            data-pos-order-complete-action="view-order"
          >
            <FileClock className="h-4 w-4" />
            View Order
          </Button>
          {children}
          <Button
            type="button"
            variant="success"
            onClick={start.onClick}
            disabled={busy}
            className="gap-2"
          >
            {start.icon}
            {start.label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
